import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Typography, theme } from '@app/ui';

type FilterTagProps = {
    title: string;
    pluralTitle?: string;
    tagElements: string[];
    isHidden?: boolean;
    isOverriding?: boolean;
};

const getFilterTitleKey = (
    title: string,
): { key: string; namespace: string } | null => {
    // If title is already a translation key (contains dots), parse it
    if (title.includes('.')) {
        // Check if it's a common namespace key like "filters.accounts"
        if (title.startsWith('filters.')) {
            return { key: title, namespace: 'common' };
        }
        // Default to common namespace for other dotted keys
        return { key: title, namespace: 'common' };
    }

    const titleToKeyMap: { [key: string]: { key: string; namespace: string } } =
        {
            // English titles (canonical)
            Accounts: { key: 'filters.accounts', namespace: 'common' },
            Campaigns: { key: 'filters.campaigns', namespace: 'common' },
            'Media Type': {
                key: 'filters.mediaTypeLabel',
                namespace: 'common',
            },
            'Media Types': { key: 'filters.mediaTypes', namespace: 'common' },
            Caption: { key: 'filters.caption', namespace: 'common' },
            From: { key: 'filters.from', namespace: 'common' },
            To: { key: 'filters.to', namespace: 'common' },
            'Start Date': { key: 'filters.startDate', namespace: 'common' },
            'End Date': { key: 'filters.endDate', namespace: 'common' },
            // Japanese translations (for reverse lookup when language changes)
            アカウント: { key: 'filters.accounts', namespace: 'common' },
            キャンペーン: { key: 'filters.campaigns', namespace: 'common' },
            メディアタイプ: { key: 'filters.mediaTypes', namespace: 'common' },
            キャプション: { key: 'filters.caption', namespace: 'common' },
            開始日: { key: 'filters.startDate', namespace: 'common' },
            終了日: { key: 'filters.endDate', namespace: 'common' },
        };

    return titleToKeyMap[title] || null;
};

const FilterTag = ({
    title,
    pluralTitle,
    tagElements,
    isHidden = false,
    isOverriding = false,
}: FilterTagProps) => {
    const { t } = useTranslation('common');
    const {
        token: { colorPrimary },
    } = theme.useToken();

    const titleKeyInfo = getFilterTitleKey(title);
    let translatedTitle: string;
    if (titleKeyInfo) {
        const translated = t(titleKeyInfo.key, { ns: titleKeyInfo.namespace });
        translatedTitle =
            typeof translated === 'string' ? translated : titleKeyInfo.key;
    } else {
        translatedTitle = title;
    }

    if (isHidden) return null;

    const shouldPluralize =
        translatedTitle.charAt(translatedTitle.length - 1).toLowerCase() !==
        's';
    const pluralLabel =
        pluralTitle ??
        (shouldPluralize ? translatedTitle + 's' : translatedTitle);
    const elementsLabel =
        tagElements.length > 3
            ? `${tagElements.length} ${pluralLabel}`
            : tagElements.join(' - ');

    if (isOverriding) {
        return (
            <Tag data-test="filter-tag-overriding" color="#009fdf">
                <Typography.Text
                    style={{
                        fontWeight: '500',
                        color: 'white',
                    }}
                >
                    {translatedTitle}:{' '}
                </Typography.Text>
                <Typography.Text
                    style={{
                        color: 'white',
                    }}
                >
                    {elementsLabel}
                </Typography.Text>
            </Tag>
        );
    }

    return (
        <Tag
            data-test="filter-tag"
            color="#E6F4FF"
            style={{
                borderColor: colorPrimary,
            }}
        >
            <Typography.Text
                style={{
                    fontWeight: '500',
                    color: colorPrimary,
                }}
            >
                {translatedTitle}:{' '}
            </Typography.Text>
            <Typography.Text
                style={{
                    color: colorPrimary,
                }}
            >
                {elementsLabel}
            </Typography.Text>
        </Tag>
    );
};

export default FilterTag;
